import { Element, ConnectionPoint, Point } from '@/types/elements';

export const CONNECTION_SNAP_DISTANCE = 20;

export function getElementConnectionPoints(
  element: Element
): ConnectionPoint[] {
  switch (element.type) {
    case 'rectangle':
    case 'diamond':
    case 'icon':
    case 'svg':
    case 'image':
      return getRectangularConnectionPoints(element);

    case 'ellipse':
      return getEllipseConnectionPoints(element);

    case 'text':
      return getTextConnectionPoints(element);

    default:
      return [];
  }
}

function getRectangularConnectionPoints(
  element: Element & { width: number; height: number }
): ConnectionPoint[] {
  const { x, y, width, height } = element;

  return [
    // Corners
    { id: 'top-left', x, y, type: 'corner' },
    { id: 'top-right', x: x + width, y, type: 'corner' },
    { id: 'bottom-left', x, y: y + height, type: 'corner' },
    { id: 'bottom-right', x: x + width, y: y + height, type: 'corner' },

    // Side midpoints
    { id: 'top-center', x: x + width / 2, y, type: 'side' },
    { id: 'right-center', x: x + width, y: y + height / 2, type: 'side' },
    { id: 'bottom-center', x: x + width / 2, y: y + height, type: 'side' },
    { id: 'left-center', x, y: y + height / 2, type: 'side' },
  ];
}

function getEllipseConnectionPoints(
  element: Element & { radiusX: number; radiusY: number }
): ConnectionPoint[] {
  const { x, y, radiusX, radiusY } = element;

  // Calculate the center point of the ellipse
  const cx = x + radiusX;
  const cy = y + radiusY;

  return [
    // Cardinal points
    { id: 'top', x: cx, y: cy - radiusY, type: 'side' },
    { id: 'right', x: cx + radiusX, y: cy, type: 'side' },
    { id: 'bottom', x: cx, y: cy + radiusY, type: 'side' },
    { id: 'left', x: cx - radiusX, y: cy, type: 'side' },
  ];
}

function getTextConnectionPoints(
  element: Element & { width: number; height: number }
): ConnectionPoint[] {
  const { x, y, width, height } = element;

  return [
    // Text elements have simpler connection points
    { id: 'left', x, y: y + height / 2, type: 'side' },
    { id: 'right', x: x + width, y: y + height / 2, type: 'side' },
    { id: 'top', x: x + width / 2, y, type: 'side' },
    { id: 'bottom', x: x + width / 2, y: y + height, type: 'side' },
  ];
}

export function findNearestConnectionPoint(
  targetPoint: Point,
  element: Element,
  maxDistance: number = CONNECTION_SNAP_DISTANCE
): ConnectionPoint | null {
  const connectionPoints = getElementConnectionPoints(element);

  let nearestPoint: ConnectionPoint | null = null;
  let minDistance = maxDistance;

  for (const point of connectionPoints) {
    const distance = Math.sqrt(
      Math.pow(targetPoint.x - point.x, 2) +
        Math.pow(targetPoint.y - point.y, 2)
    );

    if (distance < minDistance) {
      minDistance = distance;
      nearestPoint = point;
    }
  }

  return nearestPoint;
}

export function findNearestElement(
  targetPoint: Point,
  elements: Element[],
  excludeIds: string[] = [],
  maxDistance: number = CONNECTION_SNAP_DISTANCE
): { element: Element; connectionPoint: ConnectionPoint } | null {
  let nearestMatch: {
    element: Element;
    connectionPoint: ConnectionPoint;
  } | null = null;
  let minDistance = maxDistance;

  for (const element of elements) {
    if (excludeIds.includes(element.id)) continue;

    // Skip line and arrow elements for connections
    if (element.type === 'line' || element.type === 'arrow') continue;

    const connectionPoint = findNearestConnectionPoint(
      targetPoint,
      element,
      maxDistance
    );
    if (connectionPoint) {
      const distance = Math.sqrt(
        Math.pow(targetPoint.x - connectionPoint.x, 2) +
          Math.pow(targetPoint.y - connectionPoint.y, 2)
      );

      if (distance < minDistance) {
        minDistance = distance;
        nearestMatch = { element, connectionPoint };
      }
    }
  }

  return nearestMatch;
}

export function getConnectionPointPosition(
  element: Element,
  pointId: string
): Point | null {
  const connectionPoints = getElementConnectionPoints(element);
  const point = connectionPoints.find(p => p.id === pointId);
  return point ? { x: point.x, y: point.y } : null;
}

export function updateConnectedLines(
  movedElement: Element,
  allElements: Element[]
): void {
  allElements.forEach(element => {
    if (element.type === 'line' || element.type === 'arrow') {
      let needsUpdate = false;
      const updates: any = {};

      // Check start connection
      if (element.startConnection?.elementId === movedElement.id) {
        const newPosition = getConnectionPointPosition(
          movedElement,
          element.startConnection.pointId
        );
        if (newPosition) {
          needsUpdate = true;

          const newPoints = [...(element.points || [])];
          newPoints[0] = newPosition;
          updates.points = newPoints;
        }
      }

      // Check end connection
      if (element.endConnection?.elementId === movedElement.id) {
        const newPosition = getConnectionPointPosition(
          movedElement,
          element.endConnection.pointId
        );
        if (newPosition) {
          needsUpdate = true;

          const newPoints = updates.points || [...(element.points || [])];
          newPoints[newPoints.length - 1] = newPosition;
          updates.points = newPoints;
        }
      }

      if (needsUpdate) {
        Object.assign(element, updates, { updatedAt: Date.now() });
      }
    }
  });
}
